import { Check, ChevronsUpDown } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { useSchemaEditor } from "../..";
import { ISchemaParameter, StringFormat, stringFormats } from "../schema-parameter-store";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface Props {
  parameter: ISchemaParameter;
}

const StringParameterContent = observer(({ parameter }: Props) => {
  const editor = useSchemaEditor();
  const [open, setOpen] = useState(false);
  const { isReadonly } = editor;
  const handleSelect = (newValue: string) => {
    setOpen(false);
    parameter.setFormat(newValue);
  };

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="string-constraints" className="text-xs">
        <AccordionTrigger className="font-normal text-gray-700">
          String Constraints
        </AccordionTrigger>
        <AccordionContent>
          <div className="grid grid-cols-4 gap-4 sm:grid-cols-2 md:grid-cols-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Min Length</label>
              <Input
                value={parameter.minLength ?? ""}
                onChange={e => parameter.setMinLength(e.target.value)}
                readOnly={isReadonly}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Max Length</label>
              <Input
                value={parameter.maxLength ?? ""}
                onChange={e => parameter.setMaxLength(e.target.value)}
                readOnly={isReadonly}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Pattern</label>
              <Input
                value={parameter.pattern ?? ""}
                onChange={e => parameter.setPattern(e.target.value)}
                readOnly={isReadonly}
              />
            </div>
            <Popover open={open} onOpenChange={setOpen} modal>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="justify-between text-sm">
                  {parameter.format
                    ? stringFormats.find(format => format === parameter.format) || parameter.format
                    : "Select format..."}
                  <ChevronsUpDown className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <Command>
                  <CommandInput
                    placeholder="Select format..."
                    className="h-9"
                    value={parameter.format || ""}
                    onValueChange={value => parameter.setFormat(value)}
                  />
                  <CommandList>
                    <CommandEmpty>No format selected</CommandEmpty>
                    <CommandGroup>
                      {stringFormats.map(format => (
                        <CommandItem key={format} value={format} onSelect={handleSelect}>
                          {format}
                          <Check
                            className={cn(
                              "ml-auto",
                              parameter.format === format ? "opacity-100" : "opacity-0",
                            )}
                          />
                        </CommandItem>
                      ))}
                      {stringFormats.includes(parameter.format as StringFormat) ? null : (
                        <CommandItem onSelect={handleSelect}>{parameter.format}</CommandItem>
                      )}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
});

export { StringParameterContent };
